import React, { FunctionComponent, MouseEvent, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeartbeat, faNoteSticky } from '@fortawesome/pro-light-svg-icons';
import { faTriangleExclamation, faCircleStar, faInfinity } from '@fortawesome/pro-solid-svg-icons';
import {
    faBabyCarriage,
    faBone,
    faBrain,
    faEar,
    faHeart,
    faKidneys,
    faLungs,
    faPaw,
    faScalpel,
    faSmile,
    faVial,
    faVirus
} from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import styles from './ListPetInfo.module.scss';
import PetAvatar from '../PetAvatar/PetAvatar';
import moment from 'moment-timezone';
import { Popup } from './Popup/Popup';
import { useDispatch, useSelector } from 'react-redux';
import { useApiClientWrapper } from 'hooks';
import { RootState } from 'store';
import { getPetThunk } from '@spike/pets-action';
import { PetListItem } from '@spike/appointments-list-model';
import Pet from '@spike/pet-model';
import Spinner from 'components/UI/V3/Spinner';
import { Chip } from 'components/UI/V3/Chip/Chip';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { fetchMembershipsForPetIdThunk, MembershipsStatus } from '@spike/memberships-action';
import { useMediaQuery } from 'react-responsive';
import { ActionsPopup } from 'components/UI/ActionsPopup/ActionsPopup';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import PetStatusIndicators from './PetStatusIndicators';

interface Props {
    orderId: number;
    pet: PetListItem;
    className?: string;
    avatarClassName?: string;
    onClick?: () => void;
}

export const ListPetInfo: FunctionComponent<Props> = props => {
    const dispatch = useDispatch();
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const [mobilePopup, setMobilePopup] = useState<null | 'medical' | 'behavior' | 'membership' | 'notes'>(null);
    const apiClientWrapper = useApiClientWrapper();
    const petState = useSelector<RootState, Pet | undefined>(state => state.pets.pet);
    const membershipsState = useSelector((state: RootState) => state.memberships);
    const [callPetService, setCallPetService] = useState(false);
    const [callMembershipService, setCallMembershipService] = useState(false);

    useEffect(() => {
        if (callPetService) {
            dispatch(getPetThunk(apiClientWrapper, props.pet.id));
            setCallPetService(false);
        }
        // eslint-disable-next-line
    }, [callPetService]);

    useEffect(() => {
        if (callMembershipService) {
            dispatch(fetchMembershipsForPetIdThunk(apiClientWrapper, props.pet.id));
            setCallMembershipService(false);
        }
    }, [callMembershipService]);

    const clickHandler = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        props.onClick?.();
    };

    const renderMobilePopupContent = () => {
        switch (mobilePopup) {
            case 'medical':
                return medicalConditionInfo();
            case 'behavior':
                return negativePersonalitiesInfo();
            case 'membership':
                return membershipInfo();
            case 'notes':
                return notesInfo();
            default:
                return null;
        }
    };

    const medicalConditionInfo = () => {
        const categoryIcons: Record<number, IconProp> = {
            1: faHeart,
            2: faLungs,
            3: faKidneys,
            4: faBone,
            5: faVirus,
            6: faVial,
            7: faBrain,
            8: faBabyCarriage,
            9: faEar,
            10: faScalpel,
            11: faSmile,
            12: faPaw
        };

        const grouped = petState?.medicalHistory?.medicalConditions?.reduce((acc, c) => {
            if (!acc[c.medicalConditionCategoryId]) {
                acc[c.medicalConditionCategoryId] = {
                    categoryName: c.categoryName,
                    conditions: []
                };
            }
            if (c.conditionName) {
                acc[c.medicalConditionCategoryId].conditions.push(c.conditionName);
            }
            return acc;
        }, {} as Record<number, { categoryName: string; conditions: string[] }>);

        return (
            <div className={styles.medicalConditionsInfoContainer}>
                {!isMobile && <div className={styles.title}>Medical Conditions</div>}
                {grouped && Object.keys(grouped).length > 0 ? (
                    <OverlayScrollbarsComponent style={{ height: '90%', overflow: 'auto' }}>
                        {Object.entries(grouped).map(([catId, { categoryName, conditions }]) => (
                            <div key={catId}>
                                <div className={styles.categoryTitle}>
                                    {categoryIcons[Number(catId)] && (
                                        <FontAwesomeIcon
                                            icon={categoryIcons[Number(catId)]}
                                            className={styles.icon}
                                        />
                                    )}
                                    <label>{categoryName}</label>
                                </div>
                                <ul className={styles.subCategoriesContainer}>
                                    {petState?.medicalHistory?.medicalConditions
                                        ?.filter(c => c.medicalConditionCategoryId === Number(catId))
                                        .map((cond, idx) => (
                                            <li
                                                key={idx}
                                                className={styles.subCategories}
                                            >
                                                {cond.conditionName}
                                                {Number(catId) === 10 && cond.explanation && (
                                                    <div className={styles.explanation}>{cond.explanation}</div>
                                                )}
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        ))}
                    </OverlayScrollbarsComponent>
                ) : (
                    <div className={styles.spinnerContainer}>
                        <Spinner size={32} />
                    </div>
                )}
            </div>
        );
    };

    const negativePersonalitiesInfo = () => (
        <div className={styles.negativePersonalitiesInfoContainer}>
            {!isMobile && <div className={styles.title}>Special Care</div>}
            <OverlayScrollbarsComponent style={{ height: '60px', overflow: 'auto' }}>
                <ul className={styles.subCategoriesContainer}>
                    {petState?.negativePersonalities && petState.negativePersonalities.length > 0 ? (
                        petState.negativePersonalities.map(p => (
                            <li
                                key={p.id}
                                className={styles.subCategories}
                            >
                                <Chip label={p.name} />
                            </li>
                        ))
                    ) : (
                        <div className={styles.spinnerContainer}>
                            <Spinner size={32} />
                        </div>
                    )}
                </ul>
            </OverlayScrollbarsComponent>
        </div>
    );

    const membershipInfo = () => {
        const { status, list = [] } = membershipsState;

        if (status === MembershipsStatus.GettingForPetId) {
            return (
                <div className={styles.membershipsInfoContainer}>
                    {!isMobile && <div className={styles.membershipsTitle}>Applicable Memberships</div>}
                    <div className={styles.spinnerContainer}>
                        <Spinner size={32} />
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.membershipsInfoContainer}>
                {!isMobile && <div className={styles.membershipsTitle}>Applicable Memberships</div>}

                {list.length > 0 ? (
                    <OverlayScrollbarsComponent style={{ height: '95%', overflow: 'auto', overflowX: 'hidden' }}>
                        {list.map(item => {
                            const { expirationDate } = item;
                            const formattedExpiration =
                                expirationDate && moment(expirationDate).isValid()
                                    ? moment(expirationDate).format('MM/YYYY')
                                    : null;

                            return (
                                <div
                                    key={item.id}
                                    className={styles.membershipBlock}
                                >
                                    <div className={styles.membershipName}>{item.name}</div>
                                    {formattedExpiration && (
                                        <div className={styles.membershipExpiration}>Expires {formattedExpiration}</div>
                                    )}
                                    {item.membershipItems.map(membershipItem => (
                                        <div
                                            key={membershipItem.id}
                                            className={styles.itemRow}
                                        >
                                            <span className={styles.itemName}>• {membershipItem.itemName}</span>
                                            <div className={styles.membershipPill}>
                                                <FontAwesomeIcon
                                                    icon={faCircleStar}
                                                    className={styles.starIcon}
                                                />
                                                {String(membershipItem.total).toLowerCase() === 'unlimited' ? (
                                                    <FontAwesomeIcon icon={faInfinity} />
                                                ) : (
                                                    <span>
                                                        {membershipItem.left}/{membershipItem.total}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </OverlayScrollbarsComponent>
                ) : (
                    <div className={styles.noMemberships}>No memberships applicable</div>
                )}
            </div>
        );
    };

    const notesInfo = () => (
        <div className={styles.notesInfoContainer}>
            <div className={styles.title}>{petState?.name ? `${petState.name}'s Notes` : ''}</div>
            <OverlayScrollbarsComponent style={{ height: '91%', overflow: 'auto' }}>
                <div className={styles.notesList}>
                    {petState?.notes && petState.notes.length > 0 ? (
                        petState.notes.slice(-2).map(note => (
                            <div
                                key={note.id}
                                className={styles.noteItem}
                            >
                                <div className={styles.noteText}>{note.body ?? (note as any).message}</div>
                                <div className={styles.noteDate}>
                                    {note.createdAt ? moment(note.createdAt).format('MMM DD, YYYY [at] h:mm a') : ''}
                                </div>
                                <hr />
                            </div>
                        ))
                    ) : (
                        <div className={styles.spinnerContainer}>
                            <Spinner size={32} />
                        </div>
                    )}
                </div>
            </OverlayScrollbarsComponent>
        </div>
    );
    const popupHeaders: Record<string, string> = {
        medical: 'Medical Conditions',
        behavior: 'Special Care',
        membership: 'Applicable Memberships',
        notes: ''
    };

    return (
        <div
            className={clsx(styles.boxPet, props.className, {
                [styles.clickable]: !!props.onClick,
                [styles.not_clickable]: props.onClick === undefined
            })}
            onClick={clickHandler}
        >
            <PetAvatar
                petType={props.pet.petType}
                imageUrl={props.pet.imageUrl ?? undefined}
                className={props.avatarClassName}
            />
            <div className={styles.infoContainer}>
                <div className={styles.nameRow}>
                    <span className={styles.name}>{props.pet.name}</span>
                    <PetStatusIndicators
                        petId={props.pet.id}
                        petName={props.pet.name}
                    />

                    {props.pet.hasMedicalConditions &&
                        (isMobile ? (
                            <FontAwesomeIcon
                                icon={faHeartbeat}
                                className={styles.icon}
                                onClick={e => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setCallPetService(true);
                                    setMobilePopup('medical');
                                }}
                            />
                        ) : (
                            <Popup
                                placement="top"
                                content={medicalConditionInfo()}
                                onClick={() => setCallPetService(true)}
                                className={styles.medicalConditionsPopupContainer}
                            >
                                <FontAwesomeIcon
                                    icon={faHeartbeat}
                                    className={styles.icon}
                                />
                            </Popup>
                        ))}

                    {props.pet.hasNegativeTags &&
                        (isMobile ? (
                            <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                className={clsx(styles.icon, styles.specialCareIcon)}
                                onClick={e => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setCallPetService(true);
                                    setMobilePopup('behavior');
                                }}
                            />
                        ) : (
                            <Popup
                                placement="top"
                                content={negativePersonalitiesInfo()}
                                onClick={() => setCallPetService(true)}
                                className={styles.negativePopupContainer}
                            >
                                <FontAwesomeIcon
                                    icon={faTriangleExclamation}
                                    className={clsx(styles.icon, styles.specialCareIcon)}
                                />
                            </Popup>
                        ))}

                    {props.pet.hasMemberships &&
                        (isMobile ? (
                            <FontAwesomeIcon
                                icon={faCircleStar}
                                className={styles.starIcon}
                                onClick={e => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setCallMembershipService(true);
                                    setMobilePopup('membership');
                                }}
                            />
                        ) : (
                            <Popup
                                placement="top"
                                content={membershipInfo()}
                                onClick={() => setCallMembershipService(true)}
                                className={styles.membershipPopupContainer}
                            >
                                <FontAwesomeIcon
                                    icon={faCircleStar}
                                    className={styles.starIcon}
                                />
                            </Popup>
                        ))}
                </div>

                {props.pet.breed && (
                    <div className={styles.breedRow}>
                        <span className={styles.breed}>{props.pet.breed.name}</span>
                    </div>
                )}

                {props.pet.notesCount && props.pet.notesCount !== 0 ? (
                    <div className={styles.notesQtyContainer}>
                        {isMobile ? (
                            <div
                                className={styles.notesRow}
                                onClick={e => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setCallPetService(true);
                                    setMobilePopup('notes');
                                }}
                            >
                                <span className={styles.notesQty}>
                                    <FontAwesomeIcon icon={faNoteSticky} /> {props.pet.notesCount} notes
                                </span>
                            </div>
                        ) : (
                            <Popup
                                placement="top"
                                content={notesInfo()}
                                onClick={() => setCallPetService(true)}
                                className={styles.notesPopupContainer}
                            >
                                <div className={styles.notesRow}>
                                    <span className={styles.notesQty}>
                                        <FontAwesomeIcon icon={faNoteSticky} /> {props.pet.notesCount} notes
                                    </span>
                                </div>
                            </Popup>
                        )}
                    </div>
                ) : null}
            </div>

            {isMobile && mobilePopup && (
                <div onClick={e => e.stopPropagation()}>
                    <ActionsPopup
                        onClose={() => setMobilePopup(null)}
                        header={
                            <div style={{ textAlign: 'center', fontWeight: 600, fontSize: 16 }}>
                                {popupHeaders[mobilePopup]}
                            </div>
                        }
                        info={renderMobilePopupContent()}
                    />
                </div>
            )}
        </div>
    );
};

export default ListPetInfo;
